import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { useAnimatedMount } from '@ui-component-library/hooks/useAnimatedMount';

import {
  drawerBackdropClasses,
  drawerContentClasses,
  drawerContentWrapperClasses,
  drawerFooterClasses,
  drawerHeaderClasses,
  grabberClasses
} from './Drawer.styles';
import type {
  DrawerContentProps,
  DrawerFooterProps,
  DrawerHeaderProps,
  DrawerProps
} from './Drawer.types';

export const Drawer = ({
  isOpen,
  onClose,
  children,
  className,
  size = 'content',
  hideBackdrop = false,
  ...props
}: DrawerProps) => {
  const { shouldRender, animateIn, ref: drawerRef, onTransitionEnd } = useAnimatedMount(isOpen);

  const startY = useRef<number | null>(null);
  const currentY = useRef<number>(0);
  const draggingRef = useRef(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!draggingRef.current || startY.current === null || !drawerRef.current) {
        return;
      }
      const diff = e.clientY - startY.current;
      if (diff > 0) {
        currentY.current = diff;
        setDragOffset(diff);
      }
    },
    [drawerRef]
  );

  const handlePointerUp = useCallback(
    function pointerUpHandler() {
      if (!drawerRef.current) {
        return;
      }
      setIsDragging(false);

      if (currentY.current > 100) {
        setDragOffset(window.innerHeight);
        setIsAnimatingOut(true);
      } else {
        setDragOffset(0);
      }

      draggingRef.current = false;
      startY.current = null;
      currentY.current = 0;

      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', pointerUpHandler);
    },
    [handlePointerMove, drawerRef]
  );

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!drawerRef.current) {
      return;
    }

    /* eslint-disable no-undef */
    const currentTransform = new WebKitCSSMatrix(
      window.getComputedStyle(drawerRef.current).transform
    ).f;
    startY.current = e.clientY - currentTransform;
    draggingRef.current = true;

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleDrawerTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    onTransitionEnd(e);

    if (e.target !== drawerRef.current) {
      return;
    }

    if (isAnimatingOut) {
      onClose();
      setDragOffset(0);
      setIsAnimatingOut(false);
    }

    /* eslint-disable no-undef */
    const currentTransform = new WebKitCSSMatrix(
      window.getComputedStyle(drawerRef.current).transform
    ).f;
    if (currentTransform < 10) {
      setIsDragging(true);
    }
  };

  const getTransform = () => {
    if (dragOffset > 0) {
      return `translateY(${dragOffset}px)`;
    }
    return animateIn ? 'translateY(0)' : 'translateY(100%)';
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={cn(drawerBackdropClasses({ hideBackdrop }))}
      style={{
        opacity: animateIn ? 1 : 0,
        visibility: shouldRender ? 'visible' : 'hidden',
        transition: 'opacity 300ms ease-in-out'
      }}
      onClick={() => onClose()}
    >
      <div
        ref={drawerRef}
        className={cn(drawerContentClasses({ size }), className)}
        style={{
          transform: getTransform(),
          transition: isDragging ? 'none' : 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={handleDrawerTransitionEnd}
        {...props}
      >
        <div className={cn(grabberClasses(), className)} onPointerDown={handlePointerDown} />
        {children}
      </div>
    </div>
  );
};

const DrawerHeader = ({ children, className, ...props }: DrawerHeaderProps) => (
  <div className={cn(drawerHeaderClasses(), className)} {...props}>
    {children}
  </div>
);

const DrawerContent = ({ children, className, ...props }: DrawerContentProps) => (
  <div className={cn(drawerContentWrapperClasses(), className)} {...props}>
    {children}
  </div>
);

const DrawerFooter = ({ children, className, ...props }: DrawerFooterProps) => (
  <div className={cn(drawerFooterClasses(), className)} {...props}>
    {children}
  </div>
);

Drawer.Header = DrawerHeader;
Drawer.Content = DrawerContent;
Drawer.Footer = DrawerFooter;
